<script setup>
import { ref, computed, watch, onMounted } from "vue";
import {
  getCodigosDefecto,
  crearCodigoDefecto,
  actualizarCodigoDefecto,
  eliminarCodigoDefecto,
  subirImagenCodigoDefecto,
  eliminarImagenCodigoDefecto,
} from "@/utils/api_backend";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";
import { usePaginacionCodigosDefectoStore } from "@/store/paginacionStore";
import CrudToolbar from "@/components/Shared/CrudToolbar.vue";
import ColumnFilterMenu from "@/components/Shared/ColumnFilterMenu.vue";

const paginacionStore = usePaginacionCodigosDefectoStore();

const registros = ref([]);
const filtro = ref("");
const loading = ref(false);
const guardando = ref(false);
const editando = ref(false);
const paginaActual = ref(1);
const modalElement = ref(null);
let modalInstance = null;

const form = ref({ id: null, codigo: "", descripcion: "", imagenes: [] });

// ── Ordenamiento ───────────────────────────────────────────
const sortKey = ref("codigo");
const sortDir = ref("asc");

function onSort({ field, asc }) {
  sortKey.value = field;
  sortDir.value = asc ? "asc" : "desc";
  paginaActual.value = 1;
}

watch(filtro, () => {
  paginaActual.value = 1;
});

// ── Computed ───────────────────────────────────────────────
const filtrados = computed(() => {
  const s = filtro.value.toLowerCase();
  return registros.value.filter(
    (r) => String(r.codigo).includes(s) || r.descripcion.toLowerCase().includes(s)
  );
});

const ordenados = computed(() =>
  [...filtrados.value].sort((a, b) => {
    // El campo 'codigo' es numérico — ordenar como número
    if (sortKey.value === "codigo") {
      const diff = a.codigo - b.codigo;
      return sortDir.value === "asc" ? diff : -diff;
    }
    const cmp = String(a[sortKey.value] ?? "").localeCompare(
      String(b[sortKey.value] ?? ""),
      "es",
      { sensitivity: "base" }
    );
    return sortDir.value === "asc" ? cmp : -cmp;
  })
);

const porPagina = computed(() => paginacionStore.porPagina);
const totalPaginas = computed(() =>
  Math.max(1, Math.ceil(ordenados.value.length / porPagina.value))
);
const paginados = computed(() => {
  const inicio = (paginaActual.value - 1) * porPagina.value;
  return ordenados.value.slice(inicio, inicio + porPagina.value);
});
const rangoInicio = computed(() =>
  ordenados.value.length === 0 ? 0 : (paginaActual.value - 1) * porPagina.value + 1
);
const rangoFin = computed(() =>
  Math.min(paginaActual.value * porPagina.value, ordenados.value.length)
);
const paginasVisibles = computed(() => {
  const total = totalPaginas.value;
  const actual = paginaActual.value;
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, total, actual]);
  if (actual > 1) set.add(actual - 1);
  if (actual < total) set.add(actual + 1);
  const ordenadas = [...set].sort((a, b) => a - b);
  const resultado = [];
  for (let i = 0; i < ordenadas.length; i++) {
    if (i > 0 && ordenadas[i] - ordenadas[i - 1] > 1) resultado.push("...");
    resultado.push(ordenadas[i]);
  }
  return resultado;
});

function cambiarPorPagina(n) {
  paginacionStore.setPorPagina(n);
  paginaActual.value = 1;
}

// ── CRUD ───────────────────────────────────────────────────
async function cargar() {
  loading.value = true;
  try {
    registros.value = await getCodigosDefecto();
  } catch {
    Swal.fire("Error", "No se pudieron cargar los códigos de defecto", "error");
  } finally {
    loading.value = false;
  }
}

function abrirModal(item = null) {
  limpiarImagenesPendientes();
  if (item) {
    editando.value = true;
    form.value = {
      id: item.id,
      codigo: item.codigo,
      descripcion: item.descripcion,
      imagenes: [...(item.imagenes || [])],
    };
  } else {
    editando.value = false;
    form.value = { id: null, codigo: "", descripcion: "", imagenes: [] };
  }
  modalInstance.show();
}

async function guardar() {
  guardando.value = true;
  try {
    const payload = {
      codigo: Number(form.value.codigo),
      descripcion: form.value.descripcion,
    };

    let codigoDefectoId = form.value.id;
    if (editando.value) {
      await actualizarCodigoDefecto(codigoDefectoId, payload);
    } else {
      const creado = await crearCodigoDefecto(payload);
      codigoDefectoId = creado.id;
    }

    // Las imágenes solo se sincronizan con el servidor hasta este punto,
    // una vez que el código en sí ya se guardó correctamente.
    for (const imagenId of imagenesAEliminar.value) {
      await eliminarImagenCodigoDefecto(imagenId);
    }
    for (const pendiente of imagenesNuevas.value) {
      const fd = new FormData();
      fd.append("imagen", pendiente.file);
      await subirImagenCodigoDefecto(codigoDefectoId, fd);
    }

    limpiarImagenesPendientes();
    await cargar();
    modalInstance.hide();
    Swal.fire({
      icon: "success",
      title: "Operación exitosa",
      text: `Código de defecto ${
        editando.value ? "actualizado" : "registrado"
      } correctamente`,
      timer: 2000,
      showConfirmButton: false,
    });
  } catch {
    Swal.fire("Error", "No se pudo guardar el código de defecto", "error");
  } finally {
    guardando.value = false;
  }
}

async function confirmarBorrado(item) {
  const res = await Swal.fire({
    title: `¿Desactivar código ${item.codigo}?`,
    text: `"${item.descripcion}" será marcado como inactivo.`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Sí, desactivar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "var(--d-brown)",
    cancelButtonColor: "var(--d-green)",
  });
  if (res.isConfirmed) {
    try {
      await eliminarCodigoDefecto(item.id);
      await cargar();
      Swal.fire({
        icon: "success",
        title: "Listo",
        text: "Código desactivado",
        timer: 1800,
        showConfirmButton: false,
      });
    } catch {
      Swal.fire("Error", "No se pudo desactivar el código de defecto", "error");
    }
  }
}

// ── Imágenes de evidencia — modal visor (solo lectura) ──────
const modalImagenesElement = ref(null);
let modalImagenesInstance = null;
const defectoImagenes = ref(null); // código de defecto activo en el visor
const currentImageIndex = ref(0);

const imagenActual = computed(() => {
  const imagenes = defectoImagenes.value?.imagenes || [];
  return imagenes[currentImageIndex.value] || null;
});

function abrirModalImagenes(item) {
  defectoImagenes.value = item;
  currentImageIndex.value = 0;
  modalImagenesInstance.show();
}

function imagenAnterior() {
  const total = defectoImagenes.value?.imagenes?.length || 0;
  if (total === 0) return;
  currentImageIndex.value = (currentImageIndex.value - 1 + total) % total;
}

function imagenSiguiente() {
  const total = defectoImagenes.value?.imagenes?.length || 0;
  if (total === 0) return;
  currentImageIndex.value = (currentImageIndex.value + 1) % total;
}

// ── Imágenes de evidencia — alta/edición ────────────────────
// No se sube/elimina nada del servidor aquí: solo se arma una cola local
// (imagenesNuevas / imagenesAEliminar) que se aplica recién en guardar(),
// para no dejar imágenes huérfanas si el usuario cierra el modal sin
// crear/actualizar el código.
const fileRefImagen = ref(null);
const imagenesNuevas = ref([]); // [{ localId, file, previewUrl }]
const imagenesAEliminar = ref([]); // [id, id, ...] de imágenes existentes

const imagenesVisibles = computed(() => {
  const existentes = (form.value.imagenes || [])
    .filter((img) => !imagenesAEliminar.value.includes(img.id))
    .map((img) => ({ tipo: "existente", key: `e-${img.id}`, id: img.id, src: img.imagen }));
  const nuevas = imagenesNuevas.value.map((p) => ({
    tipo: "nueva",
    key: `n-${p.localId}`,
    id: p.localId,
    src: p.previewUrl,
  }));
  return [...existentes, ...nuevas];
});

function agregarArchivos(files) {
  for (const file of Array.from(files || [])) {
    if (!file.type.startsWith("image/")) continue;
    imagenesNuevas.value.push({
      localId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file,
      previewUrl: URL.createObjectURL(file),
    });
  }
}

function handleSeleccionarImagenes(e) {
  agregarArchivos(e.target.files);
  if (fileRefImagen.value) fileRefImagen.value.value = "";
}

const arrastrandoImagen = ref(false);

function handleDropImagenes(e) {
  arrastrandoImagen.value = false;
  agregarArchivos(e.dataTransfer?.files);
}

function eliminarMiniatura(item) {
  if (item.tipo === "nueva") {
    const pendiente = imagenesNuevas.value.find((p) => p.localId === item.id);
    if (pendiente) URL.revokeObjectURL(pendiente.previewUrl);
    imagenesNuevas.value = imagenesNuevas.value.filter((p) => p.localId !== item.id);
  } else {
    imagenesAEliminar.value = [...imagenesAEliminar.value, item.id];
  }
}

function limpiarImagenesPendientes() {
  imagenesNuevas.value.forEach((p) => URL.revokeObjectURL(p.previewUrl));
  imagenesNuevas.value = [];
  imagenesAEliminar.value = [];
}

onMounted(() => {
  cargar();
  modalInstance = new Modal(modalElement.value);
  modalImagenesInstance = new Modal(modalImagenesElement.value);
  // Si el usuario cierra el modal sin guardar (X, backdrop, Esc), las
  // imágenes en cola no deben quedar seleccionadas para la próxima vez.
  modalElement.value.addEventListener("hidden.bs.modal", limpiarImagenesPendientes);
});
</script>

<template>
  <div class="crud-container" data-aos="fade-right">
    <CrudToolbar
      title="Códigos de defecto"
      v-model="filtro"
      searchPlaceholder="Buscar por código o descripción..."
    >
      <template #actions>
        <button class="btn btn-nuevo btn-header" @click="abrirModal(null)">
          <i class="bi bi-plus-circle-fill"></i>
          <span class="btn-header-texto ms-2">Nuevo código</span>
        </button>
      </template>
    </CrudToolbar>

    <!-- TABLA ESCRITORIO -->
    <div class="divisan-table-container shadow-lg d-none d-lg-block">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-verde"></div>
      </div>
      <table v-else class="table table-divisan align-middle mb-0">
        <thead>
          <tr>
            <th class="ps-4" style="width: 120px">
              <ColumnFilterMenu
                field="codigo"
                label="Código"
                type="number"
                :filterable="false"
                :items="filtrados"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="onSort"
              />
            </th>
            <th>
              <ColumnFilterMenu
                field="descripcion"
                label="Descripción"
                :filterable="false"
                :items="filtrados"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="onSort"
              />
            </th>
            <th class="text-center">Evidencias</th>
            <th class="text-center">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in paginados" :key="item.id">
            <td class="ps-4">
              <span class="badge-codigo">{{ item.codigo }}</span>
            </td>
            <td class="fw-bold text-cafe">{{ item.descripcion }}</td>
            <td class="text-center">
              <button
                v-if="item.imagenes?.length"
                class="btn-ver-imagenes"
                @click="abrirModalImagenes(item)"
              >
                <i class="bi bi-images me-1"></i>Ver imágenes
                <span class="badge-conteo-imagenes">{{ item.imagenes.length }}</span>
              </button>
              <span v-else class="chip-sin-imagenes">Sin imágenes</span>
            </td>
            <td class="text-center">
              <div class="d-flex justify-content-center gap-2">
                <button class="btn-circular" @click="abrirModal(item)" title="Editar">
                  <i class="bi bi-pencil-square"></i>
                </button>
                <button
                  class="btn-circular text-danger"
                  @click="confirmarBorrado(item)"
                  title="Desactivar"
                >
                  <i class="bi bi-trash3-fill"></i>
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="paginados.length === 0 && !loading">
            <td colspan="4" class="text-center py-5 text-cafe opacity-50 fw-bold">
              No se encontraron códigos de defecto
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Paginación escritorio -->
      <div class="paginacion-wrapper">
        <div class="pag-izq">
          <small class="pag-info"
            >{{ rangoInicio }}–{{ rangoFin }} de {{ ordenados.length }} códigos</small
          >
          <div class="por-pagina-wrap">
            <label class="por-pagina-label">Filas:</label>
            <select
              class="por-pagina-select"
              :value="porPagina"
              @change="cambiarPorPagina(Number($event.target.value))"
            >
              <option v-for="n in paginacionStore.opcionesPorPagina" :key="n" :value="n">
                {{ n }}
              </option>
            </select>
          </div>
        </div>
        <div class="pag-der">
          <button
            class="btn-pag"
            :disabled="paginaActual === 1"
            @click="paginaActual = 1"
            title="Primera"
          >
            <i class="bi bi-chevron-double-left"></i>
          </button>
          <button class="btn-pag" :disabled="paginaActual === 1" @click="paginaActual--">
            <i class="bi bi-chevron-left"></i>
          </button>
          <template v-for="p in paginasVisibles" :key="p">
            <span v-if="p === '...'" class="pag-ellipsis">…</span>
            <button
              v-else
              :class="['btn-pag-num', { activo: p === paginaActual }]"
              @click="paginaActual = p"
            >
              {{ p }}
            </button>
          </template>
          <button
            class="btn-pag"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual++"
          >
            <i class="bi bi-chevron-right"></i>
          </button>
          <button
            class="btn-pag"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual = totalPaginas"
            title="Última"
          >
            <i class="bi bi-chevron-double-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- CARDS MÓVIL -->
    <div class="d-lg-none">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-verde"></div>
      </div>
      <div
        v-else
        v-for="item in paginados"
        :key="'mob-' + item.id"
        class="divisan-mobile-card mb-3 p-3 shadow-sm"
      >
        <div class="d-flex align-items-center gap-3 mb-2">
          <span class="badge-codigo">{{ item.codigo }}</span>
          <span class="fw-bold text-cafe card-name">{{ item.descripcion }}</span>
        </div>
        <div class="mb-2">
          <button
            v-if="item.imagenes?.length"
            class="btn-ver-imagenes"
            @click="abrirModalImagenes(item)"
          >
            <i class="bi bi-images me-1"></i>Ver imágenes
            <span class="badge-conteo-imagenes">{{ item.imagenes.length }}</span>
          </button>
          <span v-else class="chip-sin-imagenes">Sin imágenes</span>
        </div>
        <div class="d-flex justify-content-end gap-3 pt-2 border-top">
          <i
            class="bi bi-pencil-square text-verde fs-5"
            style="cursor: pointer"
            @click="abrirModal(item)"
          ></i>
          <i
            class="bi bi-trash3-fill text-danger fs-5"
            style="cursor: pointer"
            @click="confirmarBorrado(item)"
          ></i>
        </div>
      </div>
      <div class="paginacion-mobile">
        <div class="pag-mobile-top">
          <small class="pag-info"
            >{{ rangoInicio }}–{{ rangoFin }} de {{ ordenados.length }} códigos</small
          >
          <div class="por-pagina-wrap">
            <label class="por-pagina-label">Filas:</label>
            <select
              class="por-pagina-select"
              :value="porPagina"
              @change="cambiarPorPagina(Number($event.target.value))"
            >
              <option v-for="n in paginacionStore.opcionesPorPagina" :key="n" :value="n">
                {{ n }}
              </option>
            </select>
          </div>
        </div>
        <div class="pag-mobile-nav">
          <button
            class="btn-pag-mobile"
            :disabled="paginaActual === 1"
            @click="paginaActual--"
          >
            <i class="bi bi-chevron-left"></i> Anterior
          </button>
          <small class="text-verde fw-bold"
            >{{ paginaActual }} / {{ totalPaginas }}</small
          >
          <button
            class="btn-pag-mobile"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual++"
          >
            Siguiente <i class="bi bi-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- MODAL -->
    <div class="modal fade" tabindex="-1" aria-hidden="true" ref="modalElement">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content divisan-modal border-0">
          <div class="modal-header border-0 pb-0">
            <h5 class="modal-title fw-bold text-cafe">
              {{ editando ? "Editar código de defecto" : "Nuevo código de defecto" }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <form @submit.prevent="guardar">
            <div class="modal-body">
              <div class="mb-3">
                <label class="form-label label-divisan"
                  >Código numérico <span class="text-danger">*</span></label
                >
                <input
                  v-model="form.codigo"
                  type="number"
                  min="1"
                  class="form-control divisan-input-modal"
                  required
                  placeholder="Ej. 81"
                />
                <small class="text-muted"
                  >Identificador numérico único del defecto.</small
                >
              </div>
              <div class="mb-3">
                <label class="form-label label-divisan"
                  >Descripción <span class="text-danger">*</span></label
                >
                <input
                  v-model="form.descripcion"
                  type="text"
                  class="form-control divisan-input-modal"
                  required
                  placeholder="Ej. Piezas incompletas"
                />
              </div>

              <div class="mb-2">
                <label class="form-label label-divisan">Imágenes de evidencia</label>

                <div v-if="imagenesVisibles.length" class="miniaturas-grid mb-2">
                  <div
                    v-for="item in imagenesVisibles"
                    :key="item.key"
                    class="miniatura-item"
                    :class="{ 'miniatura-pendiente': item.tipo === 'nueva' }"
                  >
                    <img :src="item.src" alt="Evidencia" />
                    <span v-if="item.tipo === 'nueva'" class="miniatura-badge">Nueva</span>
                    <button
                      type="button"
                      class="miniatura-eliminar"
                      title="Quitar imagen"
                      @click="eliminarMiniatura(item)"
                    >
                      <i class="bi bi-x-lg"></i>
                    </button>
                  </div>
                </div>
                <p v-else class="text-cafe opacity-40 small mb-2">
                  Aún no hay imágenes registradas.
                </p>

                <div
                  class="upload-zone-img"
                  :class="{ 'is-dragover': arrastrandoImagen }"
                  @click="fileRefImagen.click()"
                  @dragover.prevent="arrastrandoImagen = true"
                  @dragleave.prevent="arrastrandoImagen = false"
                  @drop.prevent="handleDropImagenes"
                >
                  <input
                    ref="fileRefImagen"
                    type="file"
                    accept="image/*"
                    multiple
                    class="d-none"
                    @change="handleSeleccionarImagenes"
                  />
                  <i class="bi bi-cloud-arrow-up"></i>
                  <span>Agregar imágenes <small>(arrastra o haz clic)</small></span>
                </div>
                <small class="text-muted d-block mt-1"
                  >Las imágenes se guardan hasta que confirmes con el botón de abajo.</small
                >
              </div>
            </div>
            <div class="modal-footer border-0 pt-0">
              <button
                type="submit"
                class="btn btn-nuevo w-100 py-2"
                :disabled="guardando"
              >
                <span
                  v-if="guardando"
                  class="spinner-border spinner-border-sm me-2"
                ></span>
                {{ editando ? "Actualizar código" : "Guardar código" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- MODAL IMÁGENES (carrusel) -->
    <div class="modal fade" tabindex="-1" aria-hidden="true" ref="modalImagenesElement">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content divisan-modal border-0">
          <div class="modal-header border-0 pb-0">
            <div>
              <h5 class="modal-title fw-bold text-cafe mb-0">Imágenes del defecto</h5>
              <small class="text-cafe opacity-60 fw-bold"
                >{{ defectoImagenes?.codigo }} — {{ defectoImagenes?.descripcion }}</small
              >
            </div>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body" v-if="defectoImagenes">
            <!-- Carrusel -->
            <div v-if="defectoImagenes.imagenes?.length" class="carrusel-wrap">
              <div class="carrusel-viewport">
                <img
                  v-if="imagenActual"
                  :src="imagenActual.imagen"
                  :alt="`Imagen ${currentImageIndex + 1}`"
                  class="carrusel-img"
                />
                <button
                  v-if="defectoImagenes.imagenes.length > 1"
                  type="button"
                  class="carrusel-flecha carrusel-flecha-izq"
                  @click="imagenAnterior"
                  aria-label="Anterior"
                >
                  <i class="bi bi-chevron-left"></i>
                </button>
                <button
                  v-if="defectoImagenes.imagenes.length > 1"
                  type="button"
                  class="carrusel-flecha carrusel-flecha-der"
                  @click="imagenSiguiente"
                  aria-label="Siguiente"
                >
                  <i class="bi bi-chevron-right"></i>
                </button>
              </div>

              <div class="carrusel-footer">
                <span class="carrusel-contador"
                  >{{ currentImageIndex + 1 }} / {{ defectoImagenes.imagenes.length }}</span
                >
              </div>
            </div>
            <p v-else class="text-cafe opacity-40 small mb-0 text-center py-4">
              Sin imágenes registradas
            </p>
          </div>
          <div class="modal-footer border-0 pt-0">
            <button type="button" class="btn btn-nuevo px-4" data-bs-dismiss="modal">
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.text-cafe {
  color: var(--d-brown);
}
.text-verde {
  color: var(--d-green);
}

.btn-nuevo {
  background-color: var(--d-brown);
  color: var(--d-cream);
  border: none;
  font-weight: 600;
}
.btn-nuevo:hover:not(:disabled) {
  background-color: #703808;
  color: var(--d-cream);
}
.btn-header {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.divisan-card {
  background-color: var(--d-cream);
  border-radius: 12px;
  border-left: 5px solid var(--d-green);
}
.divisan-input {
  background-color: white !important;
  border: 1px solid var(--d-green);
  color: var(--d-brown) !important;
  border-radius: 8px;
}
.search-wrapper {
  position: relative;
}
.search-wrapper i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--d-green);
  z-index: 5;
  pointer-events: none;
}
.search-wrapper input {
  padding-left: 35px;
}

.divisan-table-container {
  background-color: var(--d-cream);
  border-radius: var(--d-radius-lg);
  box-shadow: var(--d-elevation-2);
  overflow: hidden;
}
.table-divisan thead th {
  background: var(--d-cream);
  color: var(--d-brown);
  border-bottom: 1px solid var(--d-border);
}
.table-divisan th {
  padding: 15px;
  font-weight: 800;
  text-transform: capitalize;
  border: none;
  white-space: nowrap;
}
.table-divisan td {
  border-bottom: 1px solid rgba(96, 108, 56, 0.1);
  padding: 12px 15px;
}
.table-divisan tbody tr:hover td {
  background-color: rgba(96, 108, 56, 0.05);
}

.badge-codigo {
  background-color: var(--d-brown);
  color: var(--d-cream);
  padding: 4px 12px;
  border-radius: 6px;
  font-family: var(--d-font-mono);
  font-size: 0.8rem;
  font-weight: 700;
}

.btn-circular {
  background: white;
  border: 1px solid var(--d-green);
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--d-green);
  transition: 0.2s;
}
.btn-circular:hover {
  background-color: var(--d-green);
  color: var(--d-cream);
}

.divisan-modal {
  background-color: var(--d-cream);
  border: 2px solid var(--d-brown);
  border-radius: 20px;
}
.label-divisan {
  color: var(--d-brown);
  font-weight: 700;
}
.divisan-input-modal {
  border: 1px solid var(--d-green);
  border-radius: 8px;
}

.divisan-mobile-card {
  background-color: var(--d-cream);
  border-radius: 15px;
  border-left: 6px solid var(--d-brown);
}

.paginacion-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  flex-wrap: wrap;
  gap: 8px;
  border-top: 1px solid rgba(64, 34, 8, 0.1);
  background: rgba(96, 108, 56, 0.05);
}
.pag-izq {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}
.pag-der {
  display: flex;
  align-items: center;
  gap: 4px;
}
.pag-info {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--d-green);
  white-space: nowrap;
}
.por-pagina-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}
.por-pagina-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--d-green);
  white-space: nowrap;
}
.por-pagina-select {
  appearance: none;
  -webkit-appearance: none;
  background: white
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23606c38' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E")
    no-repeat right 8px center;
  border: 1.5px solid var(--d-green);
  border-radius: 8px;
  padding: 3px 26px 3px 10px;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--d-brown);
  cursor: pointer;
  outline: none;
  transition: border-color 0.15s;
  min-width: 58px;
}
.por-pagina-select:focus,
.por-pagina-select:hover {
  border-color: var(--d-brown);
}
.btn-pag {
  background: white;
  border: 1.5px solid rgba(96, 108, 56, 0.35);
  color: var(--d-brown);
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s;
  font-size: 0.8rem;
}
.btn-pag:hover:not(:disabled) {
  background: var(--d-green);
  color: var(--d-cream);
  border-color: var(--d-green);
}
.btn-pag:disabled {
  opacity: 0.3;
  cursor: default;
}
.btn-pag-num {
  background: white;
  border: 1.5px solid rgba(96, 108, 56, 0.25);
  color: var(--d-brown);
  border-radius: 8px;
  min-width: 32px;
  height: 32px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s;
  font-size: 0.82rem;
  font-weight: 700;
}
.btn-pag-num:hover {
  background: rgba(96, 108, 56, 0.1);
  border-color: var(--d-green);
}
.btn-pag-num.activo {
  background: var(--d-brown);
  color: var(--d-cream);
  border-color: var(--d-brown);
  pointer-events: none;
}
.pag-ellipsis {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 32px;
  font-size: 0.85rem;
  color: var(--d-brown);
  opacity: 0.4;
  font-weight: 700;
}

.paginacion-mobile {
  border-top: 1px solid rgba(64, 34, 8, 0.15);
  padding: 10px 4px;
  margin-top: 4px;
}
.pag-mobile-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}
.pag-mobile-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.btn-pag-mobile {
  background: var(--d-cream);
  border: 1px solid var(--d-green);
  color: var(--d-brown);
  border-radius: 10px;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 110px;
  justify-content: center;
}
.btn-pag-mobile:hover:not(:disabled) {
  background-color: var(--d-green);
  color: var(--d-cream);
}
.btn-pag-mobile:disabled {
  opacity: 0.35;
  cursor: default;
}

.card-name {
  font-size: 0.92rem;
}

@media (max-width: 991.98px) {
  .btn-header-texto {
    display: none;
  }
  .btn-header {
    width: 36px;
    height: 36px;
    padding: 0;
    justify-content: center;
    border-radius: 50%;
  }
}

@media (max-width: 768px) {
  .paginacion-wrapper {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}

/* ── Botón "Ver imágenes" ─────────────────────────────────── */
.btn-ver-imagenes {
  display: inline-flex;
  align-items: center;
  background: rgba(96, 108, 56, 0.08);
  color: var(--d-green-dark);
  border: 1px solid rgba(96, 108, 56, 0.25);
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
  transition: all 0.15s;
}
.btn-ver-imagenes:hover {
  background: var(--d-green);
  color: white;
  border-color: var(--d-green);
}
.badge-conteo-imagenes {
  background: var(--d-brown);
  color: white;
  border-radius: 20px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.68rem;
  font-weight: 800;
  margin-left: 6px;
}

/* ── Carrusel de imágenes ─────────────────────────────────── */
.carrusel-wrap {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.carrusel-viewport {
  position: relative;
  width: 100%;
  height: 320px;
  background: rgba(64, 34, 8, 0.05);
  border: 1.5px solid rgba(96, 108, 56, 0.2);
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.carrusel-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.carrusel-flecha {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(64, 34, 8, 0.15);
  color: var(--d-brown);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.15s;
}
.carrusel-flecha:hover {
  background: var(--d-brown);
  color: white;
}
.carrusel-flecha-izq {
  left: 10px;
}
.carrusel-flecha-der {
  right: 10px;
}
.carrusel-footer {
  display: flex;
  align-items: center;
  justify-content: center;
}
.carrusel-contador {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--d-brown);
  opacity: 0.7;
}

/* ── Chip "Sin imágenes" ───────────────────────────────────── */
.chip-sin-imagenes {
  display: inline-flex;
  align-items: center;
  background: rgba(64, 34, 8, 0.06);
  color: var(--d-brown);
  opacity: 0.55;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
}

/* ── Dropzone de imágenes en el modal de alta/edición ─────── */
.upload-zone-img {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1.5px dashed var(--d-green);
  border-radius: 8px;
  background: rgba(96, 108, 56, 0.05);
  padding: 8px 12px;
  cursor: pointer;
  transition: 0.2s;
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--d-brown);
}
.upload-zone-img i {
  font-size: 1rem;
  color: var(--d-green);
}
.upload-zone-img small {
  font-weight: 500;
  color: var(--d-text-muted, #8a7a63);
}
.upload-zone-img:hover,
.upload-zone-img.is-dragover {
  border-color: var(--d-green-dark);
  background: rgba(40, 54, 24, 0.07);
}

/* ── Miniaturas en el modal de alta/edición ───────────────── */
.miniaturas-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(64px, 1fr));
  gap: 8px;
}
.miniatura-item {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 8px;
  overflow: hidden;
  border: 1.5px solid rgba(96, 108, 56, 0.25);
}
.miniatura-pendiente {
  border: 1.5px dashed var(--d-green);
}
.miniatura-badge {
  position: absolute;
  bottom: 3px;
  left: 3px;
  background: var(--d-green);
  color: white;
  border-radius: 4px;
  padding: 1px 5px;
  font-size: 0.6rem;
  font-weight: 800;
  text-transform: uppercase;
}
.miniatura-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.miniatura-eliminar {
  position: absolute;
  top: 3px;
  right: 3px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: white;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  border: none;
  cursor: pointer;
  transition: background 0.15s;
}
.miniatura-eliminar:hover {
  background: #dc3545;
}
</style>
